import { useEffect, useLayoutEffect } from 'react';
import { toast } from 'react-toastify';
import { errorMsg, userListToLabelEmailPhoneValue } from '../../../../../Apis/functions';
import { GetShiftById, UpdateShift } from '../../../../../reduxToolkit/FoodieShift/FoodieShiftApi';
import { FOODIE_SHIFT_MODEL, STEPS_UPDATE_FOODIE_SHIFT } from '../Model/Models';
import { PersonChipBlack } from '../../../../../components/buttons/IconButtons';
import CommonShiftApp from './CommonShiftApp';

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Update-only hook. Loads the shift detail into the local selection lists,
 * builds the update stepper labels and resubmits the single shift body.
 * Mirrors AttendanceShift EditShiftAccessApp.
 */
export const UpdateShiftApp = () => {

    const {
        id,
        t,
        DEV,
        permission,
        emptyFields,
        disabled,
        dispatch,
        item,
        setItem,
        handleChange,
        validate,
        setSteps,

        cafeterias, setCafeterias,
        selectDeleteCafeterias, setSelectDeleteCafeterias,

        departments, setDepartments,
        selectDeleteDepartments, setSelectDeleteDepartments,

        user, setUser,
        selectDeleteUser, setSelectDeleteUser,

        steps,
        activeStep, setActiveStep,
        searchElement, setSearchElement,
        modified, setModified,
        handleStepChange,
        handleOnBack,
    } = CommonShiftApp();

    useLayoutEffect(() => {
        const stepArray = Object.values(STEPS_UPDATE_FOODIE_SHIFT)
            .filter(step => step?.TRANS && typeof step.INDEX === "number")
            .sort((a, b) => a.INDEX - b.INDEX)
            .map(step => t(step.TRANS));
        setSteps(stepArray);
    }, [t]);

    useEffect(() => {
        if (id) getData();
    }, [id]);

    useEffect(() => {
        if (modified && id) {
            getData();
            setModified(false);
        }
    }, [modified]);

    /**
     * Flags server-loaded items as already assigned. They must stay in the
     * section list (toRegister source + assigned-tab delete), but the Add tab
     * must show only the newly added ones, so it filters this flag out.
     * @param {Array<Object>} list - Mapped section items.
     * @returns {Array<Object>}
     */
    const markAssigned = (list) => (list || []).map(e => ({ ...e, isAssigned: true }));

    /**
     * Loads the shift detail by id and hydrates the form and the section
     * selection lists (cafeterias / users / departments), flagging each as
     * assigned.
     */
    const getData = () => {
        dispatch(GetShiftById(id)).then(({ payload: { data: { data } } }) => {
            setItem({ ...FOODIE_SHIFT_MODEL, id: data.id, name: data.name });
            setCafeterias(markAssigned(userListToLabelEmailPhoneValue(data.foodieCafeterias, 'id', false, null)));
            setUser(markAssigned(userListToLabelEmailPhoneValue(data.users, 'userId', true, PersonChipBlack)));
            setDepartments(markAssigned(userListToLabelEmailPhoneValue(data.departments, 'id', false, null)));
        });
    }

    /**
     * Validates, builds the shift body and updates it; on success notifies and
     * triggers a reload of the shift detail.
     */
    const handleAddEdit = () => {
        if (!validate()) return;
        const body = item.toRegister(cafeterias, departments, user);
        dispatch(UpdateShift(body)).then(({ payload: { data: { success, data, code } } }) => {
            if (success && data?.id) {
                toast.success(t('has_been_updated_successfully'));
                setModified(true);
            } else errorMsg(code);
        });
    }

    return {
        modified,
        setModified,
        id,
        DEV,
        permission,
        emptyFields,
        disabled,
        item,
        handleChange,

        cafeterias, setCafeterias,
        selectDeleteCafeterias, setSelectDeleteCafeterias,

        departments, setDepartments,
        selectDeleteDepartments, setSelectDeleteDepartments,

        user, setUser,
        selectDeleteUser, setSelectDeleteUser,

        searchElement, setSearchElement,
        steps,
        activeStep, setActiveStep,
        handleStepChange,
        handleAddEdit,
        handleOnBack,
    }
}
